/* eslint-disable theme-colors/no-literal-colors */
/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import shortid from 'shortid';
import { t } from '@superset-ui/core';

import {
  CHART_TYPE,
  COLUMN_TYPE,
  DIVIDER_TYPE,
  HEADER_TYPE,
  MARKDOWN_TYPE,
  ROW_TYPE,
  TABS_TYPE,
  TAB_TYPE,
  DYNAMIC_TYPE,
  BREADCRUMB_TYPE,
  IMAGE_TYPE,
  CAROUSEL_TYPE,
  BUTTON_TYPE,
  DIGITAL_CLOCK_TYPE,
  COUNTDOWN_TYPE,
} from './componentTypes';

import {
  MEDIUM_HEADER,
  BACKGROUND_TRANSPARENT,
  GRID_DEFAULT_CHART_WIDTH,
  GRID_COLUMN_COUNT,
} from './constants';

import { DEFAULT_SVG_ICON, DEFAULT_BACKGROUND_IMAGE } from './buttonDefaults';

const typeToDefaultMetaData = {
  [CHART_TYPE]: { width: GRID_DEFAULT_CHART_WIDTH, height: 50 },
  [COLUMN_TYPE]: {
    width: GRID_DEFAULT_CHART_WIDTH,
    background: BACKGROUND_TRANSPARENT,
  },
  [DIVIDER_TYPE]: null,
  [HEADER_TYPE]: {
    text: t('New header'),
    headerSize: MEDIUM_HEADER,
    background: BACKGROUND_TRANSPARENT,
  },
  [MARKDOWN_TYPE]: { width: GRID_DEFAULT_CHART_WIDTH, height: 50 },
  [ROW_TYPE]: { background: BACKGROUND_TRANSPARENT },
  [TABS_TYPE]: null,
  [TAB_TYPE]: {
    text: '',
    defaultText: t('Tab title'),
    placeholder: t('Tab title'),
  },
  [DYNAMIC_TYPE]: {
    width: GRID_COLUMN_COUNT,
    background: BACKGROUND_TRANSPARENT,
  },
  [BREADCRUMB_TYPE]: {
    width: GRID_COLUMN_COUNT,
    items: [
      { text: 'Home', href: '/superset/dashboard/', svgIcon: '' },
      { text: 'Dashboard', href: '', svgIcon: '' },
    ],
  },
  [IMAGE_TYPE]: {
    width: GRID_DEFAULT_CHART_WIDTH,
    height: 50,
    config: {
      src: '',
      alt: '',
      objectFit: 'contain',
      overrideWidth: '',
      overrideHeight: '',
      autoplay: false,
      muted: true,
      loop: false,
      controls: true,
    },
  },
  [CAROUSEL_TYPE]: {
    width: GRID_DEFAULT_CHART_WIDTH,
    height: 50,
    config: {
      images: [],
      interval: 3000,
      autoplay: true,
      pauseOnHover: true,
      showDots: true,
      showArrows: true,
      transition: 'fade',
      objectFit: 'contain',
    },
  },
  [BUTTON_TYPE]: {
    width: GRID_DEFAULT_CHART_WIDTH,
    height: 10,
    config: {
      text: 'TikTok 渠道',
      link: '',
      target: '_self',
      svgIcon: DEFAULT_SVG_ICON,
      textColor: '#295e9c',
      backgroundColor: '#f7faff',
      backgroundImage: DEFAULT_BACKGROUND_IMAGE,
      borderRadius: 6,
      fontSize: 14,
      fontWeight: '700',
      borderColor: '#4a90e2',
      borderWidth: 2,
      boxShadow: 'none',
    },
  },
  [DIGITAL_CLOCK_TYPE]: {
    width: GRID_DEFAULT_CHART_WIDTH,
    height: 15,
    config: {
      format24h: true,
      showSeconds: true,
      showDate: true,
      dateFormat: 'YYYY-MM-DD',
      timeColor: '#4755ab',
      dateColor: '#4755ab',
      backgroundColor: '#ffffff',
      backgroundImage: DEFAULT_BACKGROUND_IMAGE,
      borderColor: '#4755ab',
      borderWidth: 2,
      borderRadius: 8,
      padding: 16,
      fontFamily: "'DS-Digital Bold', monospace",
      fontSize: 48,
      fontWeight: '400',
      glowEffect: false,
      glowColor: '#4755ab',
      glowIntensity: 20,
      autoFitFont: true,
      layoutMode: 'normal',
    },
  },
  [COUNTDOWN_TYPE]: {
    width: GRID_DEFAULT_CHART_WIDTH,
    height: 15,
    config: {
      targetTime: '',
      countdownTitle: '倒计时',
      displayMode: 'dhms',
      showLabels: true,
      showTitle: true,
      showTargetTime: true,
      expiredText: '已结束',
      timeColor: '#4755ab',
      labelColor: '#4755ab',
      titleColor: '#4755ab',
      expiredColor: '#ff4757',
      backgroundColor: '#ffffff',
      backgroundImage: DEFAULT_BACKGROUND_IMAGE,
      borderColor: '#4755ab',
      borderWidth: 2,
      borderRadius: 8,
      padding: 16,
      fontFamily: "'DS-Digital Bold', monospace",
      fontSize: 48,
      fontWeight: '400',
      glowEffect: false,
      glowColor: '#4755ab',
      glowIntensity: 20,
      autoFitFont: true,
      layoutMode: 'normal',
    },
  },
};

function uuid(type) {
  return `${type}-${shortid.generate()}`;
}

export default function entityFactory(type, meta, parents = []) {
  return {
    type,
    id: uuid(type),
    children: [],
    parents,
    meta: {
      ...typeToDefaultMetaData[type],
      ...meta,
    },
  };
}
